import React from "react";
import { Box, TextField, Button, MenuItem, Typography } from "@mui/material";
import { ArrowRight, Mail, Phone, CheckCircle2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { submitLead } from "../../api/leads";
import { useSettings } from "../../context/SettingsContext";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#F8FAFF",
    borderRadius: "10px",
    fontSize: 14.5,
    "& fieldset": { borderColor: "rgba(15,23,42,0.10)" },
    "&:hover fieldset": { borderColor: "rgba(37,99,235,0.4)" },
    "&.Mui-focused": { bgcolor: "white" },
    "&.Mui-focused fieldset": { borderColor: "#2563EB", borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontSize: 14.5 },
  "& .MuiInputLabel-root.Mui-focused": { color: "#2563EB" },
};

const trustPoints = [
  "Reply within one business day",
  "Free initial consultation",
  "No spam, ever",
];

// Static list — confirm these slugs match what the backend expects for
// `enquiry_type` before shipping.
const enquiryTypes = [
  { value: "course", label: "Course Enquiry" },
  { value: "software", label: "Software Development" },
  { value: "hardware", label: "Hardware & Infrastructure" },
  { value: "placement", label: "Placement / Hiring" },
  { value: "corporate_training", label: "Corporate Training" },
  { value: "managed_services", label: "Managed IT Services" },
  { value: "other", label: "Other" },
];

export default function ContactSection() {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const settings = useSettings();
  const mutation = useMutation({
    mutationFn: (data) => submitLead({ ...data, source: "contact_page" }),
    onSuccess: ({ data }) => {
      toast.success(data.message || "Submission received. We will contact you soon.");
      reset();
    },
    onError: (err) => {
      const e = err.response?.data;
      if (e?.email) toast.error(e.email[0]);
      else if (e?.phone) toast.error(e.phone[0]);
      else if (e?.enquiry_type) toast.error(e.enquiry_type[0]);
      else toast.error("Submission failed. Please try again.");
    },
  });

  return (
    <Box component="section" id="contact" sx={{ bgcolor: "white", py: { xs: 8, md: 11 } }}>
      <div className="section-shell">
        <Box
          sx={{
            borderRadius: "22px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(15,23,42,0.06), 0 30px 60px -30px rgba(15,23,42,0.25)",
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
          }}
        >
          {/* LEFT — deep blue info panel */}
          <Box
            sx={{
              flex: { lg: "0 0 38%" },
              position: "relative",
              bgcolor: "#0F2A66",
              backgroundImage:
                "radial-gradient(ellipse 80% 60% at 0% 0%, rgba(96,165,250,0.35), transparent 60%), radial-gradient(ellipse 70% 50% at 100% 100%, rgba(37,99,235,0.4), transparent 60%)",
              color: "white",
              p: { xs: 4, sm: 5 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              minHeight: { lg: 540 },
            }}
          >
            {/* faint grid texture for continuity with other sections */}
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
                backgroundSize: "36px 36px",
                maskImage: "radial-gradient(ellipse 90% 80% at 50% 0%, black 0%, transparent 80%)",
                WebkitMaskImage: "radial-gradient(ellipse 90% 80% at 50% 0%, black 0%, transparent 80%)",
              }}
            />

            <Box sx={{ position: "relative" }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#93C5FD",
                  mb: 2,
                }}
              >
                Get in touch
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1.6rem", sm: "1.85rem" },
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.25,
                  mb: 2,
                }}
              >
                Route every inquiry to the right NETLABS workflow.
              </Typography>
              <Typography sx={{ fontSize: 14.5, color: "rgba(255,255,255,0.75)", lineHeight: 1.65, maxWidth: 360 }}>
                Students receive personalized course recommendations. Businesses are routed directly into a consultation workflow.
              </Typography>
            </Box>

            <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: 2.5, mt: 5 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {trustPoints.map((point) => (
                  <Box key={point} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <CheckCircle2 size={16} style={{ color: "#86EFAC", flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.85)" }}>{point}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ height: "1px", bgcolor: "rgba(255,255,255,0.12)" }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                {[
                  { icon: Mail, label: settings.email || "hello@netlabs.com" },
                  { icon: Phone, label: settings.main_phone || "+91 00000 00000" },
                ].map(({ icon: Icon, label }) => (
                  <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "9px",
                        bgcolor: "rgba(255,255,255,0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={14} strokeWidth={2.25} />
                    </Box>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* RIGHT — form panel */}
          <Box
            component="form"
            onSubmit={handleSubmit((d) => mutation.mutate(d))}
            sx={{
              flex: { lg: "0 0 62%" },
              bgcolor: "white",
              p: { xs: 3, sm: 5 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
              <TextField
                fullWidth
                label="Name"
                sx={fieldSx}
                {...register("name", { required: true })}
                error={!!errors.name}
                helperText={errors.name ? "Name is required" : " "}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                sx={fieldSx}
                {...register("email", { required: true })}
                error={!!errors.email}
                helperText={errors.email ? "Email is required" : " "}
              />
              <TextField
                fullWidth
                label="Phone"
                sx={fieldSx}
                {...register("phone", { required: true })}
                error={!!errors.phone}
                helperText={errors.phone ? "Phone is required" : " "}
              />
              <TextField
                select
                fullWidth
                label="Enquiry Type"
                defaultValue="course"
                sx={fieldSx}
                helperText=" "
                {...register("enquiry_type", { required: true })}
                error={!!errors.enquiry_type}
              >
                {enquiryTypes.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Requirement"
                multiline
                rows={4}
                sx={fieldSx}
                {...register("requirement_text", { required: true })}
                error={!!errors.requirement_text}
                helperText={errors.requirement_text ? "Please describe your requirement" : " "}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              endIcon={<ArrowRight size={19} />}
              disabled={mutation.isPending}
              sx={{
                mt: 0.5,
                py: 1.5,
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: 15,
                textTransform: "none",
                bgcolor: "#2563EB",
                boxShadow: "0 10px 24px -10px rgba(37,99,235,0.6)",
                transition: "all 0.18s ease",
                "&:hover": {
                  bgcolor: "#1D4ED8",
                  boxShadow: "0 12px 28px -10px rgba(37,99,235,0.65)",
                  transform: "translateY(-1px)",
                },
                "&:disabled": { bgcolor: "rgba(37,99,235,0.5)", color: "white" },
              }}
            >
              {mutation.isPending ? "Sending..." : "Send Message"}
            </Button>

            <Typography sx={{ fontSize: 12, color: "#94A3B8", textAlign: "center", mt: 1.5 }}>
              Your information is used only to respond to this inquiry.
            </Typography>
          </Box>
        </Box>
      </div>
    </Box>
  );
}